import { IncomingMessage } from 'http';

import { EventbriteHeader, Stage } from '@eventbrite/webapp-constants';
import { HAS_WINDOW } from '@eventbrite/feature-detection';

import { saveOrganizerIdInCookie } from '@/components/_shared/buttons/FollowButton/utils';

import { DEV_STAGES } from '../../common/constants';
import { type RefundPolicyProps, RefundPolicyStatus, RefundPolicyCode } from '@/types';

const EB_HOST = 'www.eventbrite.com';

export const isYTShortVerticalVideo = (url?: string) => {
  return url && isYoutubeURL(url) && url.includes('short');
};

export const isYTHorizontalVideoURL = (url: string) => {
  return isYoutubeURL(url) && !url.includes('short');
};

const isYoutubeURL = (url: string) => {
  const pattern = /^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+/;
  return pattern.test(url);
};

const getHostFromRequest = (req: IncomingMessage): string => {
  const host = req.headers[EventbriteHeader.EB_HOST];
  return (Array.isArray(host) ? host[0] : host) || EB_HOST;
};

/**
 * Gets the domain URL from the request
 * @param req - IncomingMessage object
 * @returns domain URL
 */
export const getDomainUrl = (req: IncomingMessage): string => {
  const host = getHostFromRequest(req);
  return `https://${host}`;
};

export const getDefaultDomainUrl = (): string => `https://${EB_HOST}`;

/**
 * Gets the base URL from the window.location based on environment
 * @returns base URL
 */
const getBaseUrl = (stage?: Stage): string => {
  if (stage && DEV_STAGES.includes(stage)) {
    return 'https://www.evbqa.com';
  }
  return window.location.origin;
};

/**
 * Gets the current page referrer for login redirection
 * @returns current page pathname and search params
 */
export const getReferrer = (): string | null => {
  if (!HAS_WINDOW) {
    return null;
  }
  const { pathname, search = '' } = window.location;
  return pathname + search;
};

/**
 * Redirects user to sign-in page with current page as referrer
 * @param organizerId - Optional Organizer ID to save for follow action after login
 */
export const redirectToSignIn = (organizerId?: string, stage?: Stage): void => {
  if (!HAS_WINDOW) {
    return;
  }

  if (organizerId) {
    saveOrganizerIdInCookie(organizerId);
  }

  const signInUrl = new URL(`${getBaseUrl(stage)}/signin/`);
  signInUrl.searchParams.set('referrer', getReferrer() || '');
  signInUrl.searchParams.set('stld', '1');

  window.location.href = signInUrl.toString();
};

const parseCommaSeparatedArray = (value: string | null, limit?: number): string[] => {
  if (!value) return [];

  return value
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, limit);
};

export const safeGetFromStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Generic function to get and parse a comma-separated array from localStorage
 * @param storageKey - The localStorage key to retrieve data from
 * @param limit - Optional limit for the number of items to return
 * @returns Array of parsed strings
 */
export function getParsedStorageArray(storageKey: string, limit?: number): string[] {
  const data = safeGetFromStorage(storageKey);
  return parseCommaSeparatedArray(data, limit);
}

export function getLastRecentSearchTerms(limit?: number): string[] {
  return getParsedStorageArray('search:autocomplete:history', limit);
}

export function getLastRecentViewsList(limit?: number): string[] {
  return getParsedStorageArray('user_recent_views', limit);
}

/**
 * Assigns a DOM node to a React ref if the ref is a RefObject
 *
 * @param ref - The React ref to assign to
 * @param node - The DOM node to assign
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const assignRefToNode = (ref: React.RefObject<any> | undefined, node: HTMLElement) => {
  if (ref && 'current' in ref) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ref as any).current = node;
  }
};

/**
 * Assigns multiple refs to the same DOM node
 *
 * @param refs - Array of React refs to assign to
 * @param node - The DOM node to assign to all refs
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const assignRefsToNode = (refs: (React.RefObject<any> | undefined)[], node: HTMLElement) => {
  refs.forEach((ref) => assignRefToNode(ref, node));
};

export const isUrlMatchingHost = (url: string, host: string): boolean => {
  const urlObj = new URL(url);
  return urlObj.hostname === host;
};

/**
 * Determines if an event should be indexed by search engines
 * @param url - The event URL
 * @param ebHost - The host from the request
 * @param isIndexable - Whether the event is marked as indexable in SEO settings
 * @returns true if the event should be indexed, false otherwise
 */
export const shouldIndexEvent = (
  url: string,
  ebHost: string | undefined,
  isIndexable: boolean | undefined,
): boolean => {
  if (!ebHost || !isIndexable) {
    return false;
  }
  return isUrlMatchingHost(url, ebHost);
};

export const getRefundPolicy = (refund?: RefundPolicyProps): RefundPolicyStatus => {
  const { refundPolicyCode, refundPolicyIsNoRefunds } = refund || {};
  if (refundPolicyIsNoRefunds) return RefundPolicyStatus.NOT_ACCEPTED;
  const acceptedPolicies = [
    RefundPolicyCode.FLEXIBLE,
    RefundPolicyCode.MODERATE,
    RefundPolicyCode.STRICT,
    RefundPolicyCode.CUSTOM,
  ];
  if (refundPolicyCode && acceptedPolicies.includes(refundPolicyCode as RefundPolicyCode)) {
    return RefundPolicyStatus.ACCEPTED;
  }
  return RefundPolicyStatus.NOT_DEFINED;
};
